import { DELIMITERS } from "@/constants";

import hasDelimiter from "./hasDelimiter";

export type SentenceWithTimings = {
  sentence: string;
  isMention: boolean;
  isRemoved: boolean;
  timings: [number, number];
};

/**
 * Splits an array of words into an array of sentences based on punctuation marks.
 * The timings can be both milliseconds or seconds
 *
 * @param words - The array of words to split into sentences.
 * @param timings - The timings corresponding to each word.
 * @returns An array of objects of type SentenceWithTimings, where each object represents a sentence along with its start and end timings.
 */

function timedWordsToSentence({
  words,
  times,
  mentionIndices,
}: {
  words: string[];
  times: number[];
  mentionIndices: [start: number, end: number, removed: boolean][];
}): SentenceWithTimings[] {
  // Initialize an empty array to store the sentences with timings.
  const sentencesWithTimings: SentenceWithTimings[] = [];

  // Old sentences does not have any delimiters. Let's just return every word as a sentence for now.
  const legacy = !hasDelimiter(words.join(""));
  if (legacy) {
    return words.map((w, i) => ({
      sentence: w,
      isMention: true,
      isRemoved: false,
      timings: [times[i], times?.[i + 1] || times[i]],
    }));
  }

  const { startIndices, endIndices } = mentionIndices.reduce<{
    startIndices: number[];
    endIndices: number[];
  }>(
    (acc, [start, end]) => {
      acc.startIndices.push(start);
      acc.endIndices.push(end);
      return acc;
    },
    { startIndices: [], endIndices: [] }
  );

  const isMention = (i: number) => {
    return mentionIndices.some(([start, end]) => i >= start && i <= end);
  };
  const isRemoved = (i: number) => {
    return mentionIndices.some(
      ([start, end, removed]) => i >= start && i <= end && removed
    );
  };

  // Initialize an empty array to store the words of the current sentence.
  let currentSentenceWords: string[] = [];

  // The first sentence has to start at the very first timing.
  let currentSentenceStart = times[0];

  // Loop through each word in the array of words.
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const isLastRun = i === words.length - 1;

    // Get the last character of the word.
    const lastCharacter = word[word.length - 1];

    // Check if the last character is a period, exclamation mark, or question mark, which indicates the end of a sentence.
    const isEndOfSentence =
      DELIMITERS.includes(lastCharacter) ||
      endIndices.includes(i) ||
      startIndices.includes(i + 1);

    if (isEndOfSentence) {
      // If it is the end of a sentence, add the word to the array of words for the current sentence.
      currentSentenceWords.push(word);

      // Get the start timing for the next word, we can't use this words start as the length of the words is then not within the time span.
      const currentSentenceEnd = times?.[i + 1] || times[i];

      // Add an object representing the current sentence to the array of sentences with timings.

      sentencesWithTimings.push({
        sentence: currentSentenceWords.join(" "),
        timings: [currentSentenceStart, currentSentenceEnd],
        isMention: isMention(i),
        isRemoved: isRemoved(i),
      });

      // Reset the array of words for the current sentence and update the start of the next sentence.
      currentSentenceWords = [];
      currentSentenceStart = times[i + 1];
    } else {
      // If it is not the end of a sentence, add the word to the array of words for the current sentence.
      currentSentenceWords.push(word);
    }
    // If it's our last run return whatever we have.

    if (isLastRun && currentSentenceWords.length > 0) {
      sentencesWithTimings.push({
        sentence: currentSentenceWords.join(" "),
        // Just grab the last timing
        isMention: isMention(i),
        isRemoved: isRemoved(i),
        timings: [currentSentenceStart, times[times.length - 1]],
      });
    }
  }

  return sentencesWithTimings;
}

export default timedWordsToSentence;
